<template>
  <v-card v-if="!disabled" outlined class="mb-4 stock-product-finder">
    <v-card-subtitle class="pb-0 font-weight-medium">
      {{ $t("STOCK.PRODUCT_FINDER.TITLE") }}
    </v-card-subtitle>
    <v-card-text>
      <v-row dense>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="search"
            :label="$t('GENERAL.LABELS.SEARCH')"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            @keyup.enter="searchProducts"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            v-model="filters.stock_product_department_id"
            :items="departmentOptions"
            :loading="isLoadingOptions"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.PRODUCTS.DEPARTMENT')"
            dense
            outlined
            hide-details
            clearable
            multiple
            small-chips
            deletable-chips
            @change="handleDepartmentChange"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            v-model="filters.stock_product_category_id"
            :items="categoryOptions"
            :loading="isLoadingOptions"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.PRODUCTS.CATEGORY')"
            dense
            outlined
            hide-details
            clearable
            multiple
            small-chips
            deletable-chips
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            v-model="filters.temperature_id"
            :items="temperatureOptions"
            :loading="isLoadingOptions"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.PRODUCTS.TEMPERATURE')"
            dense
            outlined
            hide-details
            clearable
            multiple
            small-chips
            deletable-chips
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="3">
          <v-autocomplete
            v-model="filters.stock_tag_ids"
            :items="tagOptions"
            :loading="isLoadingTags"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.PRODUCTS.TAGS')"
            dense
            outlined
            hide-details
            clearable
            multiple
            small-chips
            deletable-chips
            :search-input.sync="tagSearch"
            @update:search-input="loadTags"
          ></v-autocomplete>
        </v-col>
        <template v-if="enableMappingFilters">
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="filters.meat_mapping_value_id"
              :items="meatOptions"
              :loading="isLoadingOptions"
              item-text="label"
              item-value="id"
              :label="$t('DEALS.MODAL.MEAT')"
              dense
              outlined
              hide-details
              clearable
              multiple
              small-chips
              deletable-chips
              @change="handleMeatChange"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="filters.description_mapping_value_id"
              :items="filteredDescriptionOptions"
              :loading="isLoadingOptions"
              item-text="label"
              item-value="id"
              :label="$t('DEALS.MODAL.DESCRIPTION')"
              dense
              outlined
              hide-details
              clearable
              multiple
              small-chips
              deletable-chips
              :disabled="!filters.meat_mapping_value_id.length"
            ></v-autocomplete>
          </v-col>
        </template>
        <v-col cols="12" class="d-flex align-center flex-wrap">
          <v-btn color="primary" small :loading="isSearching" @click="searchProducts">
            {{ $t("STOCK.PRODUCT_FINDER.SEARCH") }}
          </v-btn>
          <v-btn text small class="ml-2" :disabled="isSearching" @click="clearFilters">
            {{ $t("STOCK.PRODUCT_FINDER.CLEAR") }}
          </v-btn>
          <span v-if="hasSearched && !isSearching" class="ml-3 text-caption grey--text">
            {{ $t("STOCK.PRODUCT_FINDER.RESULTS_COUNT", { count: results.length }) }}
          </span>
        </v-col>
      </v-row>

      <v-alert v-if="!hasSearched" type="info" dense text class="mb-0 mt-2">
        {{ searchHint }}
      </v-alert>
      <v-alert
        v-else-if="hasSearched && !isSearching && !results.length"
        type="warning"
        dense
        text
        class="mb-0 mt-2"
      >
        {{ $t("STOCK.PRODUCT_FINDER.NO_RESULTS") }}
      </v-alert>

      <v-simple-table
        v-if="hasSearched && results.length"
        dense
        class="mt-3 stock-product-finder__results"
      >
        <thead>
          <tr>
            <th>{{ $t("STOCK.PRODUCTS.CODE") }}</th>
            <th>{{ $t("STOCK.PRODUCTS.DISPLAY_NAME") }}</th>
            <th>{{ $t("STOCK.PRODUCTS.CATEGORY") }}</th>
            <th>{{ $t("STOCK.PRODUCTS.TEMPERATURE") }}</th>
            <th>{{ $t("STOCK.PRODUCTS.PACKAGING_LEVELS") }}</th>
            <th>{{ $t("STOCK.PRODUCTS.TAGS") }}</th>
            <th>{{ $t("STOCK.PRODUCTS.UNIT") }}</th>
            <th class="text-center">{{ $t("GENERAL.TABLE.OPTIONS_COLUMN") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="product in results" :key="`finder-${product.id}`">
            <td>{{ product.code || "-" }}</td>
            <td>{{ product.display_name || "-" }}</td>
            <td>{{ categoryLabel(product) }}</td>
            <td>{{ temperatureLabel(product) }}</td>
            <td>{{ packagingLevelsLabel(product) }}</td>
            <td>
              <v-chip
                v-for="tag in product.stock_tags || []"
                :key="tag.id"
                x-small
                class="ma-1"
                color="teal"
                dark
              >
                {{ tag.label || tag.name }}
              </v-chip>
              <span v-if="!(product.stock_tags || []).length">-</span>
            </td>
            <td>{{ product.primary_unit ? product.primary_unit.code : "-" }}</td>
            <td class="text-center">
              <v-btn x-small color="success" @click="addProduct(product)">
                <v-icon left x-small>mdi-plus</v-icon>
                {{ $t("STOCK.PRODUCT_FINDER.ADD_TO_LINES") }}
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { debounce } from "lodash";
import {
  GET_STOCK_PRODUCTS,
  GET_STOCK_TEMPERATURE_OPTIONS,
  GET_STOCK_TAG_OPTIONS,
} from "@/store/stock/stockProducts.module";
import { GET_STOCK_PRODUCT_CATEGORY_OPTIONS } from "@/store/stock/stockProductCategories.module";
import { GET_STOCK_PRODUCT_DEPARTMENT_OPTIONS } from "@/store/stock/stockProductDepartments.module";
import { GET_MEATS, GET_DESCRIPTIONS } from "@/store/mappingValues.module";

const emptyFilters = () => ({
  stock_product_department_id: [],
  stock_product_category_id: [],
  temperature_id: [],
  stock_tag_ids: [],
  meat_mapping_value_id: [],
  description_mapping_value_id: [],
});

export default {
  name: "StockProductLineFinder",
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    enableMappingFilters: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: "",
      filters: emptyFilters(),
      results: [],
      hasSearched: false,
      isSearching: false,
      isLoadingOptions: false,
      isLoadingTags: false,
      departmentOptions: [],
      allCategories: [],
      temperatureOptions: [],
      tagOptions: [],
      tagSearch: "",
      meatOptions: [],
      descriptionOptions: [],
    };
  },
  computed: {
    categoryOptions() {
      const departmentIds = this.filters.stock_product_department_id || [];
      if (!departmentIds.length) {
        return this.allCategories;
      }
      return this.allCategories.filter((category) =>
        departmentIds.includes(category.stock_product_department_id)
      );
    },
    filteredDescriptionOptions() {
      const meatIds = this.filters.meat_mapping_value_id || [];
      if (!meatIds.length) {
        return [];
      }
      return this.descriptionOptions.filter((item) =>
        meatIds.some((meatId) => Number(item.group_value_id) === Number(meatId))
      );
    },
    searchHint() {
      return this.enableMappingFilters
        ? this.$t("STOCK.PRODUCT_FINDER.SEARCH_HINT_WITH_MAPPING")
        : this.$t("STOCK.PRODUCT_FINDER.SEARCH_HINT");
    },
  },
  mounted() {
    this.loadFilterOptions();
    this.loadTags("");
  },
  methods: {
    categoryLabel(product) {
      return product.stock_product_category ? product.stock_product_category.label : "-";
    },
    temperatureLabel(product) {
      return product.temperature ? product.temperature.label : "-";
    },
    packagingLevelsLabel(product) {
      const levels = product.packaging_levels || [];
      if (!levels.length) {
        return "-";
      }
      return (
        levels
          .map((level) => {
            const name = level.packaging?.label || level.packaging?.name || level.packaging_name;
            if (!name || level.weight_kg == null) {
              return null;
            }
            return `${name} ${level.weight_kg}kg`;
          })
          .filter(Boolean)
          .join(" / ") || "-"
      );
    },
    handleDepartmentChange() {
      const allowedIds = this.categoryOptions.map((category) => category.id);
      this.filters.stock_product_category_id = this.filters.stock_product_category_id.filter(
        (id) => allowedIds.includes(id)
      );
    },
    hasSearchCriteria() {
      if ((this.search || "").trim().length >= 2) {
        return true;
      }
      if (this.filters.stock_product_department_id.length) {
        return true;
      }
      if (this.filters.stock_product_category_id.length) {
        return true;
      }
      if (this.filters.temperature_id.length) {
        return true;
      }
      if (this.filters.stock_tag_ids.length) {
        return true;
      }
      if (this.enableMappingFilters && this.filters.meat_mapping_value_id.length) {
        return true;
      }
      if (this.enableMappingFilters && this.filters.description_mapping_value_id.length) {
        return true;
      }
      return false;
    },
    buildApiFilters() {
      const apiFilters = {};
      if (this.filters.stock_product_department_id.length) {
        apiFilters.stock_product_department_id = [...this.filters.stock_product_department_id];
      }
      if (this.filters.stock_product_category_id.length) {
        apiFilters.stock_product_category_id = [...this.filters.stock_product_category_id];
      }
      if (this.filters.temperature_id.length) {
        apiFilters.temperature_id = [...this.filters.temperature_id];
      }
      if (this.filters.stock_tag_ids.length) {
        apiFilters.stock_tag_ids = [...this.filters.stock_tag_ids];
      }
      if (this.enableMappingFilters && this.filters.meat_mapping_value_id.length) {
        apiFilters.meat_mapping_value_id = [...this.filters.meat_mapping_value_id];
      }
      if (this.enableMappingFilters && this.filters.description_mapping_value_id.length) {
        apiFilters.description_mapping_value_id = [...this.filters.description_mapping_value_id];
      }
      apiFilters.is_active = [1];
      return apiFilters;
    },
    searchProducts() {
      if (!this.hasSearchCriteria()) {
        this.hasSearched = false;
        this.results = [];
        return;
      }
      this.isSearching = true;
      this.hasSearched = true;
      return this.$store
        .dispatch(GET_STOCK_PRODUCTS, {
          page: 1,
          itemsPerPage: 50,
          search: (this.search || "").trim().length >= 2 ? this.search.trim() : "",
          filters: this.buildApiFilters(),
        })
        .then((response) => {
          this.results = response.data || [];
        })
        .finally(() => {
          this.isSearching = false;
        });
    },
    clearFilters() {
      this.search = "";
      this.filters = emptyFilters();
      this.results = [];
      this.hasSearched = false;
    },
    addProduct(product) {
      this.$emit("add", product);
    },
    handleMeatChange() {
      const allowedIds = this.filteredDescriptionOptions.map((item) => item.id);
      this.filters.description_mapping_value_id = this.filters.description_mapping_value_id.filter((id) =>
        allowedIds.some((allowedId) => Number(allowedId) === Number(id))
      );
    },
    loadFilterOptions() {
      this.isLoadingOptions = true;
      const requests = [
        this.$store.dispatch(GET_STOCK_PRODUCT_DEPARTMENT_OPTIONS, {}),
        this.$store.dispatch(GET_STOCK_PRODUCT_CATEGORY_OPTIONS, {}),
        this.$store.dispatch(GET_STOCK_TEMPERATURE_OPTIONS),
      ];
      if (this.enableMappingFilters) {
        requests.push(this.$store.dispatch(GET_MEATS));
        requests.push(this.$store.dispatch(GET_DESCRIPTIONS));
      }
      return Promise.all(requests)
        .then(([departmentsResponse, categoriesResponse, temperaturesResponse, meatsResponse, descriptionsResponse]) => {
          this.departmentOptions = departmentsResponse.data || [];
          this.allCategories = categoriesResponse.data || [];
          this.temperatureOptions = temperaturesResponse.data || [];
          if (this.enableMappingFilters) {
            const meats = Array.isArray(meatsResponse) ? meatsResponse : [];
            this.meatOptions = meats.map((item) => ({
              id: item.id,
              label: item.value || `Value #${item.id}`,
            }));
            const descriptions = Array.isArray(descriptionsResponse) ? descriptionsResponse : [];
            this.descriptionOptions = descriptions.map((item) => ({
              id: item.id,
              group_value_id: item.group_value_id || null,
              label: item.value || `Value #${item.id}`,
            }));
          }
        })
        .finally(() => {
          this.isLoadingOptions = false;
        });
    },
    loadTags: debounce(function (search) {
      this.isLoadingTags = true;
      return this.$store
        .dispatch(GET_STOCK_TAG_OPTIONS, { search: search || "" })
        .then((response) => {
          this.tagOptions = response.data || [];
        })
        .finally(() => {
          this.isLoadingTags = false;
        });
    }, 300),
  },
};
</script>

<style scoped>
.stock-product-finder__results th {
  white-space: nowrap;
}
</style>
